/*
Pure decision core for the iPad artifact-cache gate (SP-2592); TRPCProvider's
ArtifactCacheGate is a thin driver over it. Split out so the semantics are
unit-testable (the repo has no DOM harness).
*/

import type { PersistedArtifactCache } from "./queryClientPersister";
import { artifactCacheViewer } from "./queryClientPersister";

/**
 * Whether first render may precede the snapshot restore. Offline, the cache
 * is the data source: queries fired before the restore would miss, so paint
 * waits. Online, the app paints and fetches; the background restore can't
 * overwrite fresher data (hydrate respects dataUpdatedAt), so waiting only
 * cost first paint the multi-MB read.
 */
export function initialRenderGateOpen(isOnline: boolean): boolean {
  return isOnline;
}

export type ArtifactRestoreAction =
  /** This user's, fresh — hydrate into the query cache. */
  | "hydrate"
  /** A DIFFERENT account's — purge memory + disk before anything reads it. */
  | "purge"
  /** Nothing usable (absent, or this user's but stale) — do nothing. */
  | "none";

/**
 * What to do with the persisted snapshot once identity is known. A
 * stale-but-same-user snapshot is skipped, not purged: the next persist
 * overwrites it, and purging would drop offline coverage early.
 */
export function resolveArtifactRestore(args: {
  cached: Pick<
    PersistedArtifactCache,
    "userId" | "viewerId" | "buster" | "timestamp"
  > | null;
  userId: string;
  /** The effective viewer NOW (acting-as target, else the signed-in user). */
  viewerId: string;
  /**
   * Whether the signed-in account can act as other users. A snapshot without
   * a viewer stamp predates the stamp; for an account that can impersonate,
   * its viewer is unknown and it is purged rather than assumed to be the
   * account's own. For anyone else the only possible viewer is the account.
   */
  canImpersonate: boolean;
  schemaHash: string;
  maxAgeMs: number;
  now: number;
}): ArtifactRestoreAction {
  if (args.cached == null) {
    return "none";
  }

  // Another account's snapshot, or one written for a different effective
  // viewer (an acting-as switch while the read was in flight, or a relaunch
  // under a different impersonation): never hydrate, and purge so it cannot
  // be served later.
  if (
    args.cached.userId !== args.userId ||
    artifactCacheViewer(args.cached) !== args.viewerId ||
    (args.cached.viewerId === undefined && args.canImpersonate)
  ) {
    return "purge";
  }

  const fresh =
    args.cached.buster === args.schemaHash &&
    args.now - args.cached.timestamp < args.maxAgeMs;
  return fresh ? "hydrate" : "none";
}
